import { MarketingApiError } from '../../../../api/venue-marketing';

export type MarketingAction =
  | 'save-social'
  | 'verify'
  | 'save-summary'
  | 'add-note'
  | 'edit-note'
  | 'toggle-note'
  | 'delete-note'
  | 'load-notes';

type Status = 400 | 403 | 404 | 409 | 'other';

/** Placeholder swapped for who to ask, once the venue's region is known. */
const ASK = '{ask}';
const REGION_REFUSAL = `Couldn't save. You can't edit venues outside your regions. ${ASK}`;
const VENUE_GONE = 'This venue no longer exists. Go back to the venues list.';
const NOTE_GONE = 'That note was already removed. The list has been refreshed.';
const NOTE_NOT_YOURS =
  "You can't change this note. Only its author, or someone who can edit every region, can.";
const SAVE_FAILED = "Couldn't save. Check your connection and try again.";
const BAD_FIELDS = 'Check the highlighted fields and try again.';

const NOTE_COPY: Record<Status, string> = {
  400: 'That note could not be saved. Check what you wrote and try again.',
  403: REGION_REFUSAL,
  404: VENUE_GONE,
  409: 'That changed while you were working. Reload the page and try again.',
  other: SAVE_FAILED,
};

const COPY: Record<MarketingAction, Record<Status, string>> = {
  'save-social': {
    400: BAD_FIELDS,
    403: REGION_REFUSAL,
    404: VENUE_GONE,
    409: 'That changed while you were editing. Reload the page and try again.',
    other: SAVE_FAILED,
  },
  verify: {
    400: "Couldn't mark that verified. Reload the page and try again.",
    403: `Couldn't verify. You can't edit venues outside your regions. ${ASK}`,
    404: VENUE_GONE,
    409: "There's no handle on that platform to verify. Add one first.",
    other: "Couldn't mark that verified. Check your connection and try again.",
  },
  'save-summary': {
    400: BAD_FIELDS,
    403: REGION_REFUSAL,
    404: VENUE_GONE,
    409: 'That changed while you were editing. Reload the page and try again.',
    other: SAVE_FAILED,
  },
  'add-note': NOTE_COPY,
  'edit-note': {
    ...NOTE_COPY,
    403: NOTE_NOT_YOURS,
    404: NOTE_GONE,
  },
  'toggle-note': {
    ...NOTE_COPY,
    400: "Couldn't update that item. Reload the page and try again.",
    404: NOTE_GONE,
    409: 'Only tasks can be marked done.',
  },
  'delete-note': {
    ...NOTE_COPY,
    400: "Couldn't delete that note. Reload the page and try again.",
    403: NOTE_NOT_YOURS,
    404: NOTE_GONE,
    409: "Couldn't delete that note because it changed. Reload the page and try again.",
    other: "Couldn't delete that note. Check your connection and try again.",
  },
  'load-notes': {
    400: "Couldn't load the notes. Reload the page and try again.",
    403: "You don't have access to this venue's notes.",
    404: 'This venue could not be found, so its notes could not be loaded.',
    409: 'The notes changed while loading. Reload the page to see the latest.',
    other: "Couldn't load the notes. Check your connection and try again.",
  },
};

const KNOWN: readonly number[] = [400, 403, 404, 409];

const askLine = (regionName: string | null | undefined) =>
  regionName ? `Ask the ${regionName} RD or an admin.` : 'Ask an admin.';

/** The words shown to the user for a failed request. A rejected note body keeps
 *  the server's own wording, since it says what to fix. A region refusal says
 *  who to ask, naming the venue's region when there is one. */
export function marketingErrorMessage(
  err: unknown,
  action: MarketingAction,
  regionName?: string | null,
): string {
  return baseMessage(err, action).replace(ASK, askLine(regionName));
}

function baseMessage(err: unknown, action: MarketingAction): string {
  const status: Status =
    err instanceof MarketingApiError && KNOWN.includes(err.status)
      ? (err.status as Status)
      : 'other';
  const isNoteWrite = action === 'add-note' || action === 'edit-note';
  if (status === 400 && isNoteWrite && err instanceof MarketingApiError) {
    return err.fieldErrors.body ?? err.fieldErrors._ ?? COPY[action][400];
  }
  return COPY[action][status];
}

/** A 403 or 404 means what the caller may do, or whether the venue exists, has changed. */
export function isAccessRefusal(err: unknown): boolean {
  return err instanceof MarketingApiError && (err.status === 403 || err.status === 404);
}

/** Per-field messages from a 400, keyed by dotted path. Empty for any other failure. */
export function fieldErrorsFor(err: unknown): Record<string, string> {
  return err instanceof MarketingApiError && err.status === 400 ? err.fieldErrors : {};
}
